import React, { useState, useRef, useEffect } from 'react';
import { FileText, Send, Trash2, Download, MessageCircle, Sparkles } from 'lucide-react';
import { Button, Card, FileUploadZone, Alert, Badge, NoteCard } from '../shared';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import '../../styles/design-system.css';

// Set up PDF.js worker
GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const AIChatPDF = () => {
    const [file, setFile] = useState(null);
    const [pdfText, setPdfText] = useState('');
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const messagesEndRef = useRef(null);

    const suggestedQuestions = [
        "What is this document about?",
        "Summarize the main points",
        "What are the key takeaways?",
        "Explain the conclusion"
    ];

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleFileSelected = async (files) => {
        if (files && files.length > 0) {
            const selectedFile = files[0];
            setFile(selectedFile);
            setMessages([]);
            setPdfText('');

            // Extract text from PDF
            try {
                const text = await extractTextFromPDF(selectedFile);
                setPdfText(text);

                // Add welcome message
                setMessages([{
                    role: 'assistant',
                    content: `I've loaded "${selectedFile.name}". Ask me anything about this document!`,
                    timestamp: new Date()
                }]);

                toast.success('PDF loaded successfully! Start chatting.');
            } catch (error) {
                toast.error(`Failed to load PDF: ${error.message}`);
            }
        }
    };

    const extractTextFromPDF = async (file) => {
        const arrayBuffer = await file.arrayBuffer();
        const loadingTask = getDocument({ data: arrayBuffer });
        const pdf = await loadingTask.promise;

        let fullText = '';
        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
            const page = await pdf.getPage(pageNum);
            const textContent = await page.getTextContent();
            const pageText = textContent.items.map(item => item.str).join(' ');
            fullText += pageText + '\\n';
        }

        return fullText;
    };

    const sendMessage = async (messageText = inputMessage) => {
        if (!messageText.trim() || !pdfText) return;

        const userMessage = {
            role: 'user',
            content: messageText,
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setInputMessage('');
        setIsProcessing(true);

        try {
            // Generate AI response based on PDF content
            const aiResponse = await generateAIResponse(messageText, pdfText);

            const assistantMessage = {
                role: 'assistant',
                content: aiResponse,
                timestamp: new Date()
            };

            setMessages(prev => [...prev, assistantMessage]);
        } catch (error) {
            toast.error(`Failed to get response: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    const generateAIResponse = async (question, context) => {
        // Mock AI response - Replace with actual AI API call
        await new Promise(resolve => setTimeout(resolve, 1500));

        // Simple mock responses based on question keywords
        const lowerQuestion = question.toLowerCase();

        if (lowerQuestion.includes('about') || lowerQuestion.includes('topic')) {
            return 'Based on the document, this appears to be a comprehensive text covering various important topics. The content is structured to provide detailed information and insights on the subject matter.';
        } else if (lowerQuestion.includes('summary') || lowerQuestion.includes('main points')) {
            return 'The main points of this document include:\\n• Introduction to key concepts\\n• Detailed analysis of core topics\\n• Practical applications and examples\\n• Conclusions and recommendations';
        } else if (lowerQuestion.includes('takeaway') || lowerQuestion.includes('conclusion')) {
            return 'The key takeaways from this document emphasize the importance of understanding the core concepts presented. The document concludes with actionable insights and recommendations for further exploration.';
        } else {
            return `Based on the document content, I can help answer your question about "${question}". The document provides relevant information that addresses this topic in detail.`;
        }
    };

    const clearChat = () => {
        setMessages([{
            role: 'assistant',
            content: `Chat cleared. Ask me anything about "${file.name}"!`,
            timestamp: new Date()
        }]);
        toast.success('Chat history cleared');
    };

    const exportChat = () => {
        const chatText = messages.map(msg =>
            `[${msg.role.toUpperCase()}] ${msg.content}\\n`
        ).join('\\n');

        const blob = new Blob([chatText], { type: 'text/plain' });
        const fileName = file.name.replace(/\\.pdf$/i, '');
        saveAs(blob, `${fileName}_chat.txt`);
        toast.success('Chat exported!');
    };

    return (
        <div>
            <div className="tool-header">
                <div className="tool-icon-wrapper">
                    <MessageCircle size={40} color="var(--color-text-primary)" />
                </div>
                <div className="tool-header-content">
                    <h1 className="tool-title">AI Chat with PDF</h1>
                    <p className="tool-description">
                        Have an interactive conversation with your PDF. Ask questions and get AI-powered answers based on the document content.
                    </p>
                </div>
            </div>

            {!file ? (
                <FileUploadZone
                    onFilesSelected={handleFileSelected}
                    acceptedTypes={['application/pdf']}
                    multiple={false}
                    title="Drop PDF file here"
                    description="or click to browse"
                />
            ) : (
                <>
                    <Alert variant="success" title="PDF Loaded">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                                <FileText size={16} />
                                <span>{file.name}</span>
                            </div>
                            <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
                                <Button variant="ghost" size="sm" onClick={clearChat}>
                                    <Trash2 size={16} />
                                    Clear Chat
                                </Button>
                                <Button variant="ghost" size="sm" onClick={exportChat}>
                                    <Download size={16} />
                                    Export
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => { setFile(null); setMessages([]); setPdfText(''); }}>
                                    Change PDF
                                </Button>
                            </div>
                        </div>
                    </Alert>

                    {/* Chat Messages */}
                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Card style={{ padding: 0, overflow: 'hidden' }}>
                            <div style={{
                                height: '500px',
                                overflowY: 'auto',
                                padding: 'var(--spacing-4)',
                                backgroundColor: 'var(--color-surface-elevated)'
                            }}>
                                {messages.map((msg, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            display: 'flex',
                                            justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                                            marginBottom: 'var(--spacing-3)'
                                        }}
                                    >
                                        <div style={{
                                            maxWidth: '70%',
                                            padding: 'var(--spacing-3)',
                                            borderRadius: 'var(--radius-lg)',
                                            backgroundColor: msg.role === 'user' ? 'var(--color-accent)' : 'var(--color-surface)',
                                            color: msg.role === 'user' ? 'white' : 'var(--color-text-primary)',
                                            boxShadow: 'var(--shadow-sm)',
                                            whiteSpace: 'pre-wrap'
                                        }}>
                                            {msg.role === 'assistant' && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-2)' }}>
                                                    <Sparkles size={16} style={{ color: 'var(--color-accent)' }} />
                                                    <Badge variant="info" style={{ fontSize: 'var(--text-xs)' }}>AI</Badge>
                                                </div>
                                            )}
                                            {msg.content}
                                        </div>
                                    </div>
                                ))}
                                {isProcessing && (
                                    <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 'var(--spacing-3)' }}>
                                        <div style={{
                                            padding: 'var(--spacing-3)',
                                            borderRadius: 'var(--radius-lg)',
                                            backgroundColor: 'var(--color-surface)',
                                            boxShadow: 'var(--shadow-sm)'
                                        }}>
                                            <div className="animate-pulse">AI is thinking...</div>
                                        </div>
                                    </div>
                                )}
                                <div ref={messagesEndRef} />
                            </div>

                            {/* Input Area */}
                            <div style={{
                                padding: 'var(--spacing-4)',
                                borderTop: '1px solid var(--color-border-light)',
                                backgroundColor: 'var(--color-surface)'
                            }}>
                                {/* Suggested Questions */}
                                {messages.length === 1 && (
                                    <div style={{ marginBottom: 'var(--spacing-3)' }}>
                                        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-2)' }}>
                                            Suggested questions:
                                        </div>
                                        <div style={{ display: 'flex', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
                                            {suggestedQuestions.map((question, index) => (
                                                <Button
                                                    key={index}
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => sendMessage(question)}
                                                    disabled={isProcessing}
                                                >
                                                    {question}
                                                </Button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
                                    <input
                                        type="text"
                                        value={inputMessage}
                                        onChange={(e) => setInputMessage(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && !isProcessing && sendMessage()}
                                        placeholder="Ask a question about this PDF..."
                                        className="input"
                                        disabled={isProcessing}
                                        style={{ flex: 1 }}
                                    />
                                    <Button
                                        variant="primary"
                                        onClick={() => sendMessage()}
                                        disabled={isProcessing || !inputMessage.trim()}
                                        loading={isProcessing}
                                    >
                                        <Send size={20} />
                                    </Button>
                                </div>
                            </div>
                        </Card>
                    </div>
                </>
            )}

            <NoteCard variant="info">
                <strong>Note:</strong> This AI chat analyzes your PDF content to answer questions. For best results, ask specific questions about the document's content.
            </NoteCard>
        </div>
    );
};

export default AIChatPDF;
